import { Collection } from "../../domain/Collection";

import { UserGroup } from "../../domain/UserGroup";
import { useServicesContext } from "../../providers/ServicesProvider";
import { usePaginatedList } from "../usePaginatedList/usePaginatedList";
import { DEFAULT_PAGE_SIZE, EVENT_TYPES, FAILED_OPERATION_TYPES, SWR_KEY_PREFIX } from "../../constants/constants";
import { useUpdateSync } from "../useUpdateSync/useUpdateSync";
import { difference, getInstancePermissionChanges } from "../../lib/utils";
import { useMemo } from "react";

export const useUserGroups = ({
    organizationId,
    query = "",
    useExactMatchSearch,
    pageSize = DEFAULT_PAGE_SIZE,
    productIds = undefined,
    groupType = "",
    groupTypes = [],
    instanceIds = [],
    roleIds = []
}: {
    organizationId?: string;
    query?: string;
    useExactMatchSearch?: boolean;
    pageSize?: number;
    productIds?: string[];
    groupType?: string; // todo: deprecate after `hide_product_groups` feature flag is removed
    groupTypes?: string[];
    instanceIds?: string[];
    roleIds?: string[];
}) => {
    const { userService } = useServicesContext();
    const { fireChangeEvent } = useUpdateSync();

    const key = useMemo(() => {
        if (!organizationId) return null;
        const groupTypeParam = groupType || groupTypes.join(",");
        const searchParams = new URLSearchParams();
        searchParams.set("organizationId", organizationId);
        searchParams.set("query", query);
        if (useExactMatchSearch) {
            searchParams.set("useExactMatchSearch", `${useExactMatchSearch}`);
        }
        if (instanceIds.length > 0) {
            searchParams.set("instanceIds", instanceIds.join(","));
        }
        if (groupTypeParam) {
            searchParams.set("groupTypes", groupTypeParam);
        }
        if (productIds?.length) {
            searchParams.set("productIds", productIds.join(","));
        }
        if (roleIds?.length) {
            searchParams.set("roleIds", roleIds.join(","));
        }
        return `${SWR_KEY_PREFIX.GROUPS}/api/usergroups?${searchParams.toString()}`;
    }, [organizationId, groupType, groupTypes, query, useExactMatchSearch, instanceIds, productIds, roleIds]);

    const { pageData, error, currentPage, setCurrentPage } = usePaginatedList<UserGroup>({
        key,
        fetcher: (): Promise<Collection<UserGroup>> => {
            return userService.getUserGroups({
                organizationId: organizationId as string,
                query,
                useExactMatchSearch,
                size: pageSize,
                page: currentPage,
                productIds,
                groupType,
                groupTypes,
                instanceIds,
                roleIds
            });
        }
    });

    const addUserGroup = async (newGroup: UserGroup): Promise<UserGroup> => {
        const results = await userService.addUserGroup(newGroup);

        fireChangeEvent({
            type: EVENT_TYPES.ADD_GROUP,
            data: {
                group: { id: results.id, name: results.name, description: results.description }
            }
        });

        return results;
    };

    const updateUserGroup = async ({
        previousUserGroup,
        updatedUserGroup
    }: {
        previousUserGroup: UserGroup;
        updatedUserGroup: UserGroup;
    }): Promise<{
        userGroup: UserGroup;
        failures: Record<string, Array<{ instanceId?: string; userEmail?: string; error: Error }>>;
    }> => {
        const { userGroup: results, failures } = await userService.updateUserGroup({
            userGroup: previousUserGroup,
            updatedUserGroup
        });

        const { instancePermissionsToAdd, instancePermissionsToUpdate, instancePermissionsToDelete } =
            getInstancePermissionChanges({
                previous: previousUserGroup.instancePermissions || [],
                current: updatedUserGroup.instancePermissions || []
            });

        const deletedUsers = difference(previousUserGroup.users, updatedUserGroup.users);
        const addedUsers = difference(updatedUserGroup.users, previousUserGroup.users);

        if (deletedUsers.length) {
            fireChangeEvent({
                type: EVENT_TYPES.REMOVE_USER_FROM_GROUP,
                isPartial: failures[FAILED_OPERATION_TYPES.USER_GROUP_REMOVE_USER]?.length > 0,
                data: {
                    groups: [
                        {
                            id: updatedUserGroup.id
                        }
                    ],
                    users: deletedUsers.map((email) => {
                        return { email };
                    })
                }
            });
        }

        if (addedUsers.length) {
            fireChangeEvent({
                type: EVENT_TYPES.ADD_USER_TO_GROUP,
                isPartial: failures[FAILED_OPERATION_TYPES.USER_GROUP_ADD_USER]?.length > 0,
                data: {
                    groups: [
                        {
                            id: updatedUserGroup.id
                        }
                    ],
                    users: addedUsers.map((email) => {
                        return { email };
                    })
                }
            });
        }

        if (instancePermissionsToDelete.length) {
            const deletedInstanceIds = instancePermissionsToDelete.map((p) => p.instanceId);

            fireChangeEvent({
                type: EVENT_TYPES.REMOVE_INSTANCE_FROM_GROUP,
                isPartial: failures[FAILED_OPERATION_TYPES.USER_GROUP_DELETE_INSTANCE_PERMISSION]?.length > 0,
                data: {
                    groups: [{ id: updatedUserGroup.id }],
                    instances: deletedInstanceIds.map((id) => ({ id }))
                }
            });
        }

        if (instancePermissionsToAdd?.length) {
            const addedInstanceIds = instancePermissionsToAdd.map((p) => p.instanceId);

            fireChangeEvent({
                type: EVENT_TYPES.MODIFY_INSTANCE_PERMISSIONS_IN_GROUP,
                isPartial: failures[FAILED_OPERATION_TYPES.USER_GROUP_ADD_INSTANCE_PERMISSION]?.length > 0,
                data: {
                    groups: [{ id: updatedUserGroup.id }],
                    instances: addedInstanceIds.map((id) => ({ id }))
                }
            });
        }

        if (instancePermissionsToUpdate.length > 0) {
            const updatedInstanceIds = instancePermissionsToUpdate.map((p) => p.instanceId);

            fireChangeEvent({
                type: EVENT_TYPES.MODIFY_INSTANCE_PERMISSIONS_IN_GROUP,
                isPartial: failures[FAILED_OPERATION_TYPES.USER_GROUP_UPDATE_INSTANCE_PERMISSION]?.length > 0,
                data: {
                    groups: [{ id: updatedUserGroup.id }],
                    instances: updatedInstanceIds.map((id) => ({ id }))
                }
            });
        }

        if (
            previousUserGroup.name !== updatedUserGroup.name ||
            previousUserGroup.description !== updatedUserGroup.description
        ) {
            fireChangeEvent({
                type: EVENT_TYPES.UPDATE_GROUP,
                isPartial: failures[FAILED_OPERATION_TYPES.USER_GROUP_UPDATE_METADATA]?.length > 0,
                data: {
                    group: {
                        id: updatedUserGroup.id,
                        name: updatedUserGroup.name,
                        description: updatedUserGroup.description
                    }
                }
            });
        }

        return { userGroup: results, failures };
    };

    const deleteUserGroup = async ({ userGroupId }: { userGroupId: string }) => {
        const response = await userService.deleteUserGroup({ userGroupId });

        fireChangeEvent({
            type: EVENT_TYPES.DELETE_GROUP,
            data: {
                groups: [{ id: userGroupId }]
            }
        });

        return response;
    };

    const addUserGroupUsers = async ({ userGroupId, userEmails }: { userGroupId: string; userEmails: string[] }) => {
        const response = await userService.addUserGroupUsers({ userGroupId, userEmails });

        fireChangeEvent({
            type: EVENT_TYPES.ADD_USER_TO_GROUP,
            data: {
                users: userEmails.map((email) => {
                    return { email };
                }),
                groups: [{ id: userGroupId }]
            }
        });

        return response;
    };

    const deleteUserGroupUsers = async ({ userGroupId, userEmails }: { userGroupId: string; userEmails: string[] }) => {
        const response = await userService.deleteUserGroupUsers({ userGroupId, userEmails });

        fireChangeEvent({
            type: EVENT_TYPES.REMOVE_USER_FROM_GROUP,
            data: {
                users: userEmails.map((email) => {
                    return { email, userGroupIds: [userGroupId] };
                }),
                groups: [{ id: userGroupId }]
            }
        });

        return response;
    };

    return {
        userGroups: pageData?.items,
        isLoading: !error && !pageData,
        isSearching: !error && !pageData && query.length > 2,
        error,
        currentPage,
        setCurrentPage,
        addUserGroup,
        deleteUserGroup,
        updateUserGroup,
        pageSize: pageSize,
        totalCount: pageData?.totalCount || 0,
        addUserGroupUsers,
        deleteUserGroupUsers
    };
};
